import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { api, problemMessage, unwrap } from "./client";
import type { components } from "./schema";

/**
 * Job descriptions — DESIGN §20. Beside queries.ts, which was already past one sitting; same
 * conventions: generated types, `unwrap` for the API's own error sentence, prefix invalidation.
 */

type Schemas = components["schemas"];
export type JDSummary = Schemas["JDSummaryOut"];
export type JDList = Schemas["JDListOut"];
export type JDSource = Schemas["JDSourceOut"];
export type JDDetail = Schemas["JDDetailOut"];
export type JDSkill = Schemas["JDSkillOut"];
export type JDEvent = Schemas["JDEventOut"];
export type JDSettings = Schemas["JDSettingsOut"];
export type JDUploadAccepted = Schemas["JDUploadAccepted"];
export type JDEditRequest = Schemas["JDEditRequest"];
export type JDSkillEditRequest = Schemas["JDSkillEditRequest"];
export type JDSkillCreateRequest = Schemas["JDSkillCreateRequest"];
export type JDStatusName = JDSummary["status"];

export const jdKeys = {
  all: ["jds"] as const,
  list: (status?: string) => ["jds", "list", status ?? null] as const,
  detail: (id: number) => ["jds", "detail", id] as const,
  settings: ["jds", "settings"] as const,
  text: (sourceId: number) => ["jds", "source-text", sourceId] as const,
};

/** Polls while any upload is still being read, so a new JD appears without a reload. */
export function useJDs(status?: string) {
  return useQuery({
    queryKey: jdKeys.list(status),
    queryFn: async () => unwrap(await api.GET("/jds", { params: { query: status ? { status } : {} } })),
    refetchInterval: (query) =>
      query.state.data?.sources.some((s) => s.status === "queued" || s.status === "analysing")
        ? 2_000
        : false,
  });
}

export function useJD(id: number | null) {
  return useQuery({
    queryKey: jdKeys.detail(id ?? 0),
    queryFn: async () => unwrap(await api.GET("/jds/{jd_id}", { params: { path: { jd_id: id ?? 0 } } })),
    enabled: id !== null,
  });
}

export function useJDSourceText(sourceId: number | null, enabled: boolean) {
  return useQuery({
    queryKey: jdKeys.text(sourceId ?? 0),
    queryFn: async () =>
      unwrap(
        await api.GET("/jds/sources/{source_id}/text", { params: { path: { source_id: sourceId ?? 0 } } }),
      ),
    enabled: enabled && sourceId !== null,
    staleTime: Infinity,
  });
}

function useInvalidateJDs() {
  const client = useQueryClient();
  return () => {
    void client.invalidateQueries({ queryKey: jdKeys.all });
    void client.invalidateQueries({ queryKey: ["jobs"] });
  };
}

export interface UploadJDInput {
  file: File | null;
  text: string;
  title: string;
  clientName: string;
  note: string;
  force: boolean;
}

export function useUploadJD() {
  const invalidate = useInvalidateJDs();
  return useMutation({
    mutationFn: async (input: UploadJDInput): Promise<JDUploadAccepted> => {
      // openapi-fetch serialises multipart from a plain object; the generated body type keeps the
      // field names honest, the serializer builds the form the route reads.
      const { data, error } = await api.POST("/jds/sources", {
        body: {
          file: input.file as unknown as string,
          text: input.text,
          title: input.title,
          client_name: input.clientName,
          note: input.note,
          force: input.force,
        },
        bodySerializer: () => {
          const form = new FormData();
          if (input.file) form.append("file", input.file);
          else form.append("text", input.text);
          if (input.title.trim()) form.append("title", input.title.trim());
          if (input.clientName.trim()) form.append("client_name", input.clientName.trim());
          if (input.note.trim()) form.append("note", input.note.trim());
          form.append("force", String(input.force));
          return form;
        },
      });
      if (error) throw new Error(problemMessage(error));
      if (!data) throw new Error("The API returned no body");
      return data;
    },
    onSuccess: invalidate,
  });
}

export function useReanalyse() {
  const invalidate = useInvalidateJDs();
  return useMutation({
    mutationFn: async (sourceId: number) =>
      unwrap(
        await api.POST("/jds/sources/{source_id}/reanalyse", { params: { path: { source_id: sourceId } } }),
      ),
    onSuccess: invalidate,
  });
}

/** Every write answers with the whole detail, so the page swaps it in rather than refetching. */
function useDetailWrite<T>(write: (input: T) => Promise<JDDetail>) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: write,
    onSuccess: (detail) => {
      client.setQueryData(jdKeys.detail(detail.jd.id), detail);
      void client.invalidateQueries({ queryKey: ["jds", "list"] });
    },
  });
}

export function useEditJD(id: number) {
  return useDetailWrite(async (body: JDEditRequest) =>
    unwrap(await api.PATCH("/jds/{jd_id}", { params: { path: { jd_id: id } }, body })),
  );
}

export function useAddSkill(id: number) {
  return useDetailWrite(async (body: JDSkillCreateRequest) =>
    unwrap(await api.POST("/jds/{jd_id}/skills", { params: { path: { jd_id: id } }, body })),
  );
}

export function useEditSkill(id: number) {
  return useDetailWrite(async ({ skillId, ...body }: JDSkillEditRequest & { skillId: number }) =>
    unwrap(
      await api.PATCH("/jds/{jd_id}/skills/{skill_id}", {
        params: { path: { jd_id: id, skill_id: skillId } },
        body,
      }),
    ),
  );
}

export function useMoveJD(id: number) {
  return useDetailWrite(async ({ status, reason }: { status: JDStatusName; reason?: string }) =>
    unwrap(
      await api.POST("/jds/{jd_id}/status", {
        params: { path: { jd_id: id } },
        body: { status, reason: reason ?? null },
      }),
    ),
  );
}

/** The list's ticked rows, all or none; the server names any published one and deletes nothing. */
export function useDeleteJDs() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (ids: number[]) => unwrap(await api.POST("/jds/purge", { body: { ids } })),
    onSuccess: () => void client.invalidateQueries({ queryKey: jdKeys.all }),
  });
}

/** Admin-only and irreversible; the server refuses a published JD. */
export function useDeleteJD(id: number) {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const { error } = await api.DELETE("/jds/{jd_id}", { params: { path: { jd_id: id } } });
      if (error) throw new Error(problemMessage(error));
    },
    onSuccess: () => {
      client.removeQueries({ queryKey: jdKeys.detail(id) });
      void client.invalidateQueries({ queryKey: jdKeys.all });
    },
  });
}

export function useJDSettings() {
  return useQuery({
    queryKey: jdKeys.settings,
    queryFn: async () => unwrap(await api.GET("/jds/settings", {})),
  });
}

export function useSaveJDSettings() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (values: Record<string, string>) =>
      unwrap(await api.PUT("/jds/settings", { body: { values } })),
    onSuccess: (settings) => {
      client.setQueryData(jdKeys.settings, settings);
      void client.invalidateQueries({ queryKey: ["jds", "list"] });
    },
  });
}
